import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { use_session_store } from '@/lib/session-store';
import {
  cancel_deletion_request,
  create_deletion_request,
  fetch_deletion_policy,
  fetch_my_deletion_request,
  submit_public_deletion_request,
  type DeletionRole,
  type PublicDeletionInput,
} from './api';

const key = (role: DeletionRole) => ['account-deletion', role] as const;

export function use_my_deletion_request(role: DeletionRole) {
  return useQuery({
    queryKey: key(role),
    queryFn: () => fetch_my_deletion_request(role),
    // The switch must never render stale: an admin may have approved or refused it since
    // this screen was last opened, and both change what the card says entirely.
    staleTime: 0,
    retry: 0,
  });
}

export function use_request_deletion(role: DeletionRole) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { reason?: string }) => create_deletion_request(role, input),
    // The account is closed by the time this resolves, and the token that closed it is dead.
    // Sign out deliberately rather than leaving it to the next request to fail: every cached
    // query on screen belongs to an account that no longer exists, and a console that keeps
    // showing them until something happens to 401 is showing a ghost.
    onSuccess: () => {
      use_session_store.getState().clear();
      qc.clear();
    },
  });
}

export function use_cancel_deletion(role: DeletionRole) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancel_deletion_request(role),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(role) }),
  });
}

/* ------------------------------- public page ------------------------------- */

export function use_deletion_policy() {
  return useQuery({
    queryKey: ['account-deletion', 'policy'],
    queryFn: fetch_deletion_policy,
    // Published copy that changes about never. Re-fetching it on every focus of a page
    // somebody reaches once would be pure noise.
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
}

export function use_submit_public_deletion() {
  return useMutation({
    mutationFn: (input: PublicDeletionInput) => submit_public_deletion_request(input),
  });
}
